import {useRef} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {
  getLocaleCode,
  getSeoActiveAttributes,
  getSeoActiveLocales
} from 'modules/meta/selectors'
import {getAnchor, getExactMatchCount} from 'modules/sapiSearch/selectors'
import {
  useGetAlternateLinksQuery,
  useGetBreadcrumbQuery,
  useGetLinksMetadataQuery
} from 'modules/seoApi'
import {getBreadcrumbParams, getSeoLinkParams} from 'modules/seoApi/selectors'
import {useRouter} from 'next/router'
import {buildHreflangLinks, SeoHrefLangLinks} from 'seo/hreflang'
import {extractFilterSlugsFromTaxonomy} from 'utils/server/searchSeo/getFiltersFromTaxonomy'
import {
  getIndexingMetadata,
  getSeoMetadataFromNavPathInfo,
  IndexMetadata,
  SeoMetadata
} from 'utils/server/searchSeo/getSeoMetadata'
import {getSeoSRPPath} from 'utils/server/searchSeo/getSeoSRPPath'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'

import type {PlaceAnchor} from '@findhotel/sapi'
/**
 * Retrieves the correct SEO metadata for the Seo SRP.
 * Includes robots directive and canonical URL, from seo-api with a fallback to
 * the locale and result count.
 * @returns The SEO metadata or null if not on an SEO SRP page.
 */
export const useSeoSRPMetadata = ():
  | (SeoMetadata & IndexMetadata & SeoHrefLangLinks)
  | null => {
  const {query: pathParams} = useRouter()
  const anchor = useSelector(getAnchor) as PlaceAnchor
  const locale = useSelector(getLocaleCode)
  const seoActiveLocales = useSelector(getSeoActiveLocales)
  const seoAttributes = useSelector(getSeoActiveAttributes)
  const {brand} = useBrand()
  const {formatMessage} = useIntl()
  const {placeId, taxonomy} = pathParams
  const isSeoSRP = !!anchor && !!taxonomy && typeof placeId === 'string'

  const params = useSelector(state => getSeoLinkParams(state, taxonomy))
  const {metadata} = useGetLinksMetadataQuery(params, {
    selectFromResult: result => ({
      ...result,
      metadata: result?.data
    }),
    skip: !isSeoSRP
  })

  const breadcrumbParams = useSelector(state =>
    getBreadcrumbParams(state, placeId)
  )
  const {breadcrumb} = useGetBreadcrumbQuery(breadcrumbParams, {
    selectFromResult: result => ({breadcrumb: result?.data?.breadcrumb}),
    skip: !isSeoSRP
  })

  const {links: alternateLinks} = useGetAlternateLinksQuery(params, {
    selectFromResult: result => ({links: result?.data?.links}),
    skip: !isSeoSRP
  })

  const exactMatchCountLatest = useSelector(getExactMatchCount)
  const exactMatchCount = useRef(exactMatchCountLatest) // keep initial render value

  if (!isSeoSRP) return null

  const baseUrl = brand.siteAddresses.address
  const canonicalPath = getSeoSRPPath({pathParams, seoAttributes, breadcrumb})
  const indexMetadata = getIndexingMetadata({
    canonicalPath,
    initialResultsCount: exactMatchCount.current,
    baseUrl,
    isSeoActiveLocale: seoActiveLocales.includes(locale),
    seoApiRobots: metadata?.robots
  })

  const hreflangLinks: SeoHrefLangLinks = {
    hreflangLinks: alternateLinks
      ? buildHreflangLinks(
          baseUrl,
          seoActiveLocales,
          code => alternateLinks[code]
        )
      : []
  }

  const {title, description, heading} = metadata ?? {}
  if (title && description && heading) {
    return {
      title,
      description,
      heading,
      ...indexMetadata,
      ...hreflangLinks
    }
  }

  // Fallback to generating metadata from navPathInfo when the API returns no content.
  const {navPathInfo, placeName} = anchor
  const filterSlugs = extractFilterSlugsFromTaxonomy(
    taxonomy as string[] | string | undefined
  )

  const contentMetadata = getSeoMetadataFromNavPathInfo({
    placeId,
    filterSlugs,
    locale,
    seoAttributes,
    navPathInfo,
    placeName,
    formatMessage
  })

  return {
    ...contentMetadata,
    ...indexMetadata,
    ...hreflangLinks
  }
}
